import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { MasterSelect } from "@/components/common/MasterSelect";
import { ConstructionMasters, MasterMajorClass, MasterMiddleClass } from "@/models/masters";
import { Process } from "@/models/matrix";
import { RootState } from "@/reducers/types";

type ProcessMajorClassOption = Pick<MasterMajorClass, "process_major_class_id" | "process_major_class_name">;
type ProcessMiddleClassOption = Pick<MasterMiddleClass, "process_middle_class_id" | "process_middle_class_name"> & {
  process_major_class_id: number;
};

type Props = {
  processes: Process[];
  onChange: (process: Process) => void;
};

export const ProcessSelectorGroup = ({ processes, onChange }: Props) => {
  const { t } = useTranslation();

  // processesを大分類・中分類・作業名それぞれのセレクトボックスの選択肢として表示するためのデータにする
  const { process_major_classes, process_middle_classes } = useSelector<RootState, ConstructionMasters>(
    (state: RootState) => state.construction.masters
  );
  // 大分類
  const majorClassMap = useMemo(
    () => new Map(process_major_classes.map((v) => [v.process_major_class_id, v])),
    [process_major_classes]
  );
  const [majorClassId, setMajorClassId] = useState<number>();
  const processMajorClassOptions = useMemo<ProcessMajorClassOption[]>(() => {
    if (!processes) return [];
    const majorClassOptions: ProcessMajorClassOption[] = [];
    let prevMajorClassId = 0;
    for (const process of processes) {
      const { process_major_class_id } = process;
      if (
        prevMajorClassId !== process_major_class_id &&
        !majorClassOptions.some((o) => o.process_major_class_id === process_major_class_id)
      ) {
        const m = majorClassMap.get(process_major_class_id);
        if (m) {
          const { process_major_class_id, process_major_class_name } = m;
          majorClassOptions.push({ process_major_class_id, process_major_class_name });
        }
      }

      prevMajorClassId = process.process_major_class_id;
    }

    return majorClassOptions;
  }, [processes, majorClassMap]);

  // 中分類
  const [middleClassId, setMiddleClassId] = useState<number>();
  const middleClassMap = useMemo(
    () => new Map(process_middle_classes.map((v) => [v.process_middle_class_id, v])),
    [process_middle_classes]
  );
  const processMiddleClassOptions = useMemo<ProcessMiddleClassOption[]>(() => {
    if (!processes) return [];
    const middleClassOptions: ProcessMiddleClassOption[] = [];
    for (const process of processes) {
      const { process_major_class_id, process_middle_class_id } = process;
      if (majorClassId && majorClassId !== process_major_class_id) continue;
      const m = middleClassMap.get(process_middle_class_id);
      if (m) {
        const { process_middle_class_id, process_middle_class_name } = m;
        middleClassOptions.push({
          process_middle_class_id,
          process_middle_class_name,
          process_major_class_id: process_major_class_id,
        });
      }
    }

    return middleClassOptions.filter(
      (o, i) => middleClassOptions.findIndex((v) => v.process_middle_class_id === o.process_middle_class_id) === i
    );
  }, [processes, middleClassMap, majorClassId]);

  // 作業
  const [processId, setProcessId] = useState<number>();
  const processOptions = useMemo(
    () =>
      processes.filter(
        (v) =>
          (!majorClassId || v.process_major_class_id === majorClassId) &&
          (!middleClassId || v.process_middle_class_id === middleClassId)
      ),
    [majorClassId, middleClassId, processes]
  );

  useEffect(() => {
    setMiddleClassId(null);
  }, [majorClassId]);

  useEffect(() => {
    onChange(null);
    setProcessId(null);
  }, [majorClassId, middleClassId]);

  const handleSelectProcess = useCallback(
    (id: number) => {
      setProcessId(id);
      onChange(processes.find((v) => v.process_id === id));
    },
    [processes]
  );

  return (
    <div className="flex flex-col gap-[15px]">
      <div>
        <MasterSelect
          prefix="process_major_class"
          options={processMajorClassOptions}
          value={majorClassId}
          onChange={(id) => setMajorClassId(id)}
          placeholder={t("major_class")}
          isClearable={true}
        />
      </div>
      <div>
        <MasterSelect
          prefix="process_middle_class"
          options={processMiddleClassOptions}
          value={middleClassId}
          onChange={(id) => setMiddleClassId(id)}
          placeholder={t("middle_class")}
          isClearable={true}
        />
      </div>
      <div>
        <MasterSelect
          prefix="process"
          options={processOptions}
          value={processId}
          onChange={(id) => handleSelectProcess(id)}
          placeholder={t("process_title")}
        />
      </div>
    </div>
  );
};
